import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { teamsAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Users,
  Plus,
  Truck,
  Crown,
  UserPlus,
  ChevronRight,
} from "lucide-react";

export default function Teams() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      const { data } = await teamsAPI.list();
      setTeams(data.teams || []);
    } catch (error) {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Team name is required");
      return;
    }

    setSaving(true);
    try {
      const { data } = await teamsAPI.create({ name: teamName });
      toast.success("Team created!");
      setCreateModalOpen(false);
      setTeamName("");
      navigate(`/teams/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    } finally {
      setSaving(false);
    }
  };

  const handleJoinTeam = async () => {
    if (!inviteCode.trim()) {
      toast.error("Invite code is required");
      return;
    }

    setSaving(true);
    try {
      const { data } = await teamsAPI.join(inviteCode);
      toast.success(data.message);
      setJoinModalOpen(false);
      setInviteCode("");
      loadTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join team");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-amber-500/20 text-amber-500 border-amber-500/30",
      editor: "bg-blue-500/20 text-blue-500 border-blue-500/30",
      viewer: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    };
    return styles[role] || styles.viewer;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">My Teams</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setJoinModalOpen(true)}
                data-testid="join-team-btn"
                className="border-zinc-700 hover:bg-zinc-800"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Join Team
              </Button>
              <Button
                onClick={() => setCreateModalOpen(true)}
                data-testid="create-team-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {teams.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">No Teams Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create a team to collaborate with others or join an existing team
              </p>
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setJoinModalOpen(true)}
                  className="border-zinc-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Join with Code
                </Button>
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  className="btn-tactical bg-primary text-primary-foreground"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Team
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {teams.map((team) => (
              <Card
                key={team.id}
                className="card-tactical bg-zinc-950/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                onClick={() => navigate(`/teams/${team.id}`)}
                data-testid={`team-${team.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center border border-primary/30">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-foreground">{team.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-sm border ${getRoleBadge(team.my_role)}`}>
                            {team.my_role}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {team.member_count} members
                          </span>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            {team.vehicle_count} vehicles
                          </span>
                        </div>
                        {team.owner_name && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Crown className="w-3 h-3 text-amber-500" />
                            Owner: {team.owner_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Team Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g., Trail Buddies"
                className="bg-zinc-900 border-zinc-800"
                data-testid="team-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleCreateTeam}
              disabled={saving}
              data-testid="save-team-btn"
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Team Modal */}
      <Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Join Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-code">Invite Code</Label>
              <Input
                id="invite-code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="e.g., ABC12345"
                className="bg-zinc-900 border-zinc-800 font-mono uppercase"
                data-testid="invite-code-input"
              />
              <p className="text-xs text-muted-foreground">
                Ask a team admin for the invite code
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJoinModalOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleJoinTeam}
              disabled={saving}
              data-testid="join-btn"
              className="btn-tactical bg-primary text-primary-foreground"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Join Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
